import Cookies from 'js-cookie';
import {post as POST} from './requests';
import {ACCESS_TOKEN_COOKIE_NAME, EXPIRES_AT_COOKIE_NAME} from '../constants/auth';
import {SWFT_DIGITAL_API_HOST, SWFT_DIGITAL_AUTH2_CLIENT_ID} from '../constants';
import {
    AUTH_ACCESS_TOKEN_ENDPOINT,
    AUTH_GOOGLE_OAUTH_TOKEN_ENDPOINT,
    AUTH_REVOKE_ACCESS_TOKEN_ENDPOINT,
} from '../constants/endpoints';

type AuthTokenKind = 'unknown' | 'Bearer' | 'Api-Key';

interface AuthTokenInterface {
    kind: AuthTokenKind;
    accessToken: string;
    expiresAt: number;
}

interface GetAccessTokenInterface {
    email: string;
    password: string;
    client_id: string | undefined;
}

interface LogoutBodyInterface {
    access_token: string | undefined;
    client_id: string | undefined;
}

interface AuthInterface {
    data: AuthTokenInterface;
    authLoading: boolean;
    logoutLoading: boolean;
    error: string | null;
    isAuthenticated: boolean;
}

/**
 * load = load token from <cookies | msg>
 * store = store token to cookies for later use
 */
class AuthClient {
    private token?: AuthTokenInterface;

    constructor() {
        this.loadFromCookies();
    }

    store = (token: AuthTokenInterface) => {
        Cookies.set(ACCESS_TOKEN_COOKIE_NAME, token.accessToken);
        Cookies.set(EXPIRES_AT_COOKIE_NAME, token.expiresAt.toString());
    };

    private loadFromCookies = () => {
        const at = Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
        const exp = Cookies.get(EXPIRES_AT_COOKIE_NAME);
        if (at && exp) {
            this.load({
                kind: 'Bearer',
                accessToken: at,
                expiresAt: Number(exp),
            });
        }
    };

    /**
     * allow loading arbitrary tokens
     */
    load = (token: AuthTokenInterface) => {
        this.token = token;
    };

    reset = (): void => {
        Cookies.remove(ACCESS_TOKEN_COOKIE_NAME);
        Cookies.remove(EXPIRES_AT_COOKIE_NAME);
        this.token = undefined;
    };

    getToken = (): AuthTokenInterface | undefined => {
        return this.token;
    };

    getAccessToken = async (email: string, password: string): Promise<AuthTokenInterface> => {
        if (!SWFT_DIGITAL_AUTH2_CLIENT_ID) throw new Error('SWFT_DIGITAL_AUTH2_CLIENT_ID not found');
        const body: GetAccessTokenInterface = {
            email,
            password,
            client_id: SWFT_DIGITAL_AUTH2_CLIENT_ID,
        };
        const r = await POST(SWFT_DIGITAL_API_HOST + AUTH_ACCESS_TOKEN_ENDPOINT, body);
        return {
            kind: 'Bearer',
            accessToken: r['access_token'],
            expiresAt: Number(r['expires_at']),
        };
    };

    getAccessTokenFromGoogleAuth = async (code: string): Promise<AuthTokenInterface> => {
        const body = {
            code,
            client_id: SWFT_DIGITAL_AUTH2_CLIENT_ID,
            redirect_url: window.location.origin + window.location.pathname,
        };
        const r = await POST(SWFT_DIGITAL_API_HOST + AUTH_GOOGLE_OAUTH_TOKEN_ENDPOINT, body);
        return {
            kind: 'Bearer',
            accessToken: r['access_token'],
            expiresAt: Number(r['expires_at']),
        };
    };

    logout = async () => {
        if (!this.token) return;
        if (this.token.kind === 'Api-Key') return;

        const body: LogoutBodyInterface = {
            client_id: SWFT_DIGITAL_AUTH2_CLIENT_ID,
            access_token: this.token?.accessToken,
        };
        return await POST(SWFT_DIGITAL_API_HOST + AUTH_REVOKE_ACCESS_TOKEN_ENDPOINT, body);
    };
}

const AuthClientSingleton = new AuthClient();

export {AuthClientSingleton};
export type {AuthInterface, AuthTokenInterface, GetAccessTokenInterface};
